/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { filter, map, switchMap } from 'rxjs/operators';

import { combineLatest, Observable, of } from 'rxjs';
import { isArrayNullOrEmpty } from '../../../../utils';
import { IAppState } from '../../../core/store/state';
import { SortUtils } from '../../../core/utils';
import {
  ESearchType,
  ISearchDriver,
  ISearchEntity,
  ISearchGroup,
  ISearchNonPoweredAsset,
  ISearchPoweredAsset,
  ISearchVehicle
} from '../../models';
import { ESearchActions, GetSearchEntities, GetSearchEntitiesSuccess } from '../actions';
import {
  getAllSearchNonPoweredAssetsFromHierarchy,
  getAllSearchPoweredAssetsFromHierarchy,
  getAllSearchVehiclesFromHierarchy
} from '../selectors';

@Injectable()
export class SearchEffects {
  getSearchEntities$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetSearchEntities>(ESearchActions.GetSearchEntities),
      map(action => action.payload),
      switchMap(types => this._getSearchEntities(types)),
      filter((searchEntities: ISearchEntity[]) => !isArrayNullOrEmpty(searchEntities)),
      map((searchEntities: ISearchEntity[]) => new GetSearchEntitiesSuccess(searchEntities))
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<IAppState>
  ) {}

  private _getSearchEntities(types: ESearchType[]): Observable<ISearchEntity[]> {
    // Ideally this will be one api call where we pass the types and get an array containing the correct object back
    const searchEntities$: Observable<ISearchEntity[]> = combineLatest([
      this._handleSearchVehicles(types),
      this._handleSearchDrivers(types),
      this._handleSearchGroups(types),
      this._handleSearchNonPoweredAssets(types),
      this._handleSearchPoweredAssets(types)
    ]).pipe(
      map(searchItems => {
        const flatItems = [].concat(...searchItems);
        return flatItems.sort((a: ISearchEntity, b: ISearchEntity) => SortUtils.naturalSortComparer(a.name, b.name));
      })
    );

    return searchEntities$;
  }

  _handleSearchVehicles(types: ESearchType[]): Observable<ISearchVehicle[]> {
    if (types.includes(ESearchType.Vehicle)) {
      return this._store.pipe(select(getAllSearchVehiclesFromHierarchy));
    }

    return of([]);
  }

  _handleSearchDrivers(types: ESearchType[]): Observable<ISearchDriver[]> {
    // Search type Driver is not supported
    return of([]);
  }

  _handleSearchGroups(types: ESearchType[]): Observable<ISearchGroup[]> {
    // Search type Group is not supported
    return of([]);
  }

  _handleSearchNonPoweredAssets(types: ESearchType[]): Observable<ISearchNonPoweredAsset[]> {
    if (types.includes(ESearchType.NonPoweredAsset)) {
      return this._store.pipe(select(getAllSearchNonPoweredAssetsFromHierarchy));
    }
    return of([]);
  }

  _handleSearchPoweredAssets(types: ESearchType[]): Observable<ISearchPoweredAsset[]> {
    if (types.includes(ESearchType.PoweredAsset)) {
      return this._store.pipe(select(getAllSearchPoweredAssetsFromHierarchy));
    }
    return of([]);
  }
}
